import { z } from "zod";
import { defineEndpoint } from "../endpoint.js";
import { stockQtySchema } from "../inventory.js";
import { paisa } from "../money.js";
import { PAYMENT_METHODS } from "../payments.js";
import { RETURN_CONDITIONS } from "../returns.js";
import { appVersionSchema } from "./health.js";
/**
 * POS sync, push side (docs/specs/pos-sync.md "Commands", B-43): business commands from the device's outbox, each
 * with its permanent idempotency key (`mutationId`) and gap-free `deviceSeq`.
 */
const qty = stockQtySchema.refine((v) => BigInt(v) > 0n, "Must be more than zero");
const discount = z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("amount"), paisa: paisa().nonnegative() }).strict(),
    z.object({ kind: z.literal("percent"), bp: z.int().min(0).max(10_000) }).strict(),
]);
export const APPROVAL_ACTIONS = [
    "discount_over_limit",
    "price_override",
    "refund_over_limit",
    "receiptless_return",
    "line_void",
    "bill_void",
    "no_sale",
    "cash_out",
    "pin_unlock",
];
const approval = z
    .object({ action: z.enum(APPROVAL_ACTIONS), approvedByUserId: z.uuid(), approvedAt: z.iso.datetime() })
    .strict();
/** `B1-P2-000123` (sale) or `B1-P2-R000045` (return): bill prefix, device code, the device's own sequence. */
export const billNumberSchema = z.string().regex(/^[A-Z0-9]{1,6}-P\d{1,3}-R?\d{6,}$/);
const base = {
    mutationId: z.uuid(),
    deviceSeq: z.int().min(1),
    /** Device time (UTC) when it happened. */
    occurredAt: z.iso.datetime(),
    /** Who was signed in on the device. */
    staffUserId: z.uuid(),
    approvals: z.array(approval).max(20).default([]),
};
const saleLine = z
    .object({
    lineId: z.uuid(),
    variantId: z.uuid(),
    qty,
    /** Price charged per selling unit. */
    unitPricePaisa: paisa().nonnegative(),
    /** Price in the device's catalog when sold (price variance, PRD §10.4). */
    listPricePaisa: paisa().nonnegative(),
    unitPrecision: z.int().min(0).max(3),
    taxRateBp: z.int().min(0).max(10_000),
    lineDiscount: discount.optional(),
})
    .strict();
const tender = z
    .object({
    paymentId: z.uuid(),
    method: z.enum(PAYMENT_METHODS),
    accountId: z.uuid(),
    /** Amount kept (cash: after change). */
    amountPaisa: paisa().positive(),
    tenderedPaisa: paisa().positive().optional(),
    reference: z.string().trim().min(1).max(60).optional(),
})
    .strict();
const saleTotals = z
    .object({
    subtotalPaisa: paisa(),
    lineDiscountsPaisa: paisa(),
    billDiscountPaisa: paisa(),
    taxPaisa: paisa(),
    totalPaisa: paisa(),
    /** Total + rounding: what the customer pays. */
    payablePaisa: paisa(),
})
    .strict();
export const saleCompletedSchema = z
    .object({
    ...base,
    type: z.literal("SaleCompleted"),
    schemaVersion: z.literal(1),
    payload: z
        .object({
        orderId: z.uuid(),
        billNumber: billNumberSchema,
        shiftId: z.uuid(),
        customerId: z.uuid().optional(),
        taxMode: z.enum(["inclusive", "exclusive"]),
        lines: z.array(saleLine).min(1).max(500),
        billDiscount: discount.optional(),
        roundingPaisa: paisa().min(-99).max(99),
        payments: z.array(tender).max(10),
        creditPaisa: paisa().nonnegative(),
        totals: saleTotals,
        exchangeOfReturnId: z.uuid().optional(),
        /** Exchange (B-50): the returned goods' value paid towards this bill; payments + credit + this = payable. */
        exchangeCreditPaisa: paisa().nonnegative().optional(),
        note: z.string().trim().max(500).optional(),
    })
        .strict(),
})
    .strict();
export const returnCompletedSchema = z
    .object({
    ...base,
    type: z.literal("ReturnCompleted"),
    schemaVersion: z.literal(1),
    payload: z
        .object({
        returnId: z.uuid(),
        returnNumber: billNumberSchema,
        shiftId: z.uuid(),
        /** Absent = receipt-less (needs the `receiptless_return` approval). */
        originalOrderId: z.uuid().optional(),
        customerId: z.uuid().optional(),
        reason: z.string().trim().max(200),
        lines: z
            .array(z
            .object({
            returnLineId: z.uuid(),
            orderLineId: z.uuid().optional(),
            variantId: z.uuid(),
            qty,
            refundPaisa: paisa().nonnegative(),
            condition: z.enum(RETURN_CONDITIONS),
        })
            .strict())
            .min(1)
            .max(500),
        refunds: z.array(tender).max(10),
        storeCreditPaisa: paisa().nonnegative(),
        totalRefundPaisa: paisa().nonnegative(),
        /** Cash refund rounding to whole taka (B-50): refunds + store credit + exchange = total refund + rounding. */
        roundingPaisa: paisa().min(-99).max(99).optional(),
        /** Exchange (B-50): the new bill this return's value paid for (queued before this return)… */
        exchangeOrderId: z.uuid().optional(),
        /** …and how much of the value went to it. */
        exchangePaisa: paisa().nonnegative().optional(),
    })
        .strict(),
})
    .strict();
export const shiftOpenedSchema = z
    .object({
    ...base,
    type: z.literal("ShiftOpened"),
    schemaVersion: z.literal(1),
    payload: z.object({ shiftId: z.uuid(), openingFloatPaisa: paisa().nonnegative() }).strict(),
})
    .strict();
export const shiftClosedSchema = z
    .object({
    ...base,
    type: z.literal("ShiftClosed"),
    schemaVersion: z.literal(1),
    payload: z
        .object({
        shiftId: z.uuid(),
        countedCashPaisa: paisa().nonnegative(),
        denominations: z
            .array(z.object({ valuePaisa: paisa().positive(), count: z.int().min(0) }).strict())
            .max(30),
        expectedCashPaisa: paisa(),
        billCount: z.int().min(0),
        returnCount: z.int().min(0),
        note: z.string().trim().max(500).optional(),
    })
        .strict(),
})
    .strict();
export const CASH_MOVEMENT_REASONS = ["petty_expense", "bank_drop", "float_top_up", "other"];
export const cashMovementRecordedSchema = z
    .object({
    ...base,
    type: z.literal("CashMovementRecorded"),
    schemaVersion: z.literal(1),
    payload: z
        .object({
        cashMovementId: z.uuid(),
        shiftId: z.uuid(),
        direction: z.enum(["in", "out"]),
        amountPaisa: paisa().positive(),
        reason: z.enum(CASH_MOVEMENT_REASONS),
        note: z.string().trim().max(200).optional(),
    })
        .strict(),
})
    .strict();
export const customerCreatedSchema = z
    .object({
    ...base,
    type: z.literal("CustomerCreated"),
    schemaVersion: z.literal(1),
    payload: z
        .object({
        customerId: z.uuid(),
        phone: z.string().regex(/^\+8801\d{9}$/),
        name: z.string().trim().min(1).max(120),
    })
        .strict(),
})
    .strict();
export const POS_ACTIVITIES = [
    "no_sale",
    "line_void",
    "bill_void",
    "pin_lockout",
    "pin_unlock",
    "price_override",
];
export const posActivityRecordedSchema = z
    .object({
    ...base,
    type: z.literal("PosActivityRecorded"),
    schemaVersion: z.literal(1),
    payload: z
        .object({
        activityId: z.uuid(),
        shiftId: z.uuid().optional(),
        activity: z.enum(POS_ACTIVITIES),
        details: z
            .record(z.string().max(40), z.union([z.string().max(200), z.number(), z.boolean()]))
            .optional(),
    })
        .strict(),
})
    .strict();
export const syncCommandSchema = z.discriminatedUnion("type", [
    saleCompletedSchema,
    returnCompletedSchema,
    shiftOpenedSchema,
    shiftClosedSchema,
    cashMovementRecordedSchema,
    customerCreatedSchema,
    posActivityRecordedSchema,
]);
export const SYNC_COMMAND_TYPES = syncCommandSchema.options.map((o) => o.shape.type.value);
/** What every command must at least have to be recorded at all (a bad payload is kept as `INVALID_COMMAND`). */
export const commandEnvelopeSchema = z.looseObject({
    mutationId: z.uuid(),
    deviceSeq: z.int().min(1),
    type: z.string().max(40),
    schemaVersion: z.int().min(1),
});
export const PUSH_BATCH_MAX = 100;
export const pushRequestSchema = z
    .object({
    sentAt: z.iso.datetime(),
    commands: z.array(commandEnvelopeSchema).min(1).max(PUSH_BATCH_MAX),
    /** The app's version (B-53): below the server's `minPushVersion` the push is refused (426). */
    appVersion: appVersionSchema.optional(),
})
    .strict();
// Results ------------------------------------------------------------------------------------------------------------
export const SYNC_STATUSES = ["accepted", "needs_review"];
/** Recorded facts that need no review (docs/specs/pos-sync.md "Flags"). */
export const SYNC_FLAGS = [
    "NEGATIVE_STOCK",
    "PRICE_VARIANCE",
    "PRODUCT_ARCHIVED",
    "CUSTOMER_MERGED",
    "CLOCK_SKEW",
    "LOCATION_INACTIVE",
    "VERIFICATION_PENDING",
];
/** Why a command needs a person to look at it; the held ones changed nothing yet. */
export const NEEDS_REVIEW_REASONS = [
    "STAFF_INACTIVE",
    "STAFF_NOT_PERMITTED",
    "APPROVAL_INVALID",
    "RETURN_EXCEEDS_SOLD",
    "TOTALS_MISMATCH",
    "PAYMENT_MISMATCH",
    "CREDIT_LIMIT_EXCEEDED",
    "SHIFT_NOT_OPEN",
    "SHIFT_OVERLAP",
    "SHIFT_TOTALS_MISMATCH",
    "DUPLICATE_NUMBER",
    "SEQ_REUSED",
    "ORIGINAL_NOT_FOUND",
    "RECEIPTLESS_RETURN",
    "UNKNOWN_REFERENCE",
    "DUPLICATE_ENTITY_ID",
    "INVALID_COMMAND",
    "SCHEMA_VERSION_UNSUPPORTED",
    "PROCESSING_FAILED",
    /** Sent by another device from an old database (B-52 recovery): applied as usual, a manager looks once. */
    "RECOVERED",
];
export const pushResultSchema = z.object({
    mutationId: z.uuid(),
    deviceSeq: z.int(),
    status: z.enum(SYNC_STATUSES),
    /** Whether the effects were written (`accepted` always is). */
    applied: z.boolean(),
    /** True when this command was already processed: the stored result, nothing done again. */
    replayed: z.boolean(),
    reasons: z.array(z.enum(NEEDS_REVIEW_REASONS)),
    flags: z.array(z.enum(SYNC_FLAGS)),
    /** Ids and numbers the device may show (`orderId`, `orderNumber`, `customerId`, …). */
    refs: z.record(z.string(), z.string()),
});
export const pushResponseSchema = z.object({
    /** Commands without a result were not processed: send them again. */
    results: z.array(pushResultSchema),
    serverLastSeq: z.int().min(0),
    /** Set when a command skipped a seq: resend from `expectedSeq`. */
    gap: z.object({ expectedSeq: z.int().min(1) }).nullable(),
    serverTime: z.iso.datetime(),
    clockSkewMs: z.int(),
});
export const pushCommands = defineEndpoint({
    tag: "pos-sync",
    name: "pushCommands",
    method: "POST",
    path: "/v1/pos/sync/push",
    access: { device: true },
    summary: "Up to 100 commands in deviceSeq order; each is processed once (replays return the stored result) and never dropped",
    body: pushRequestSchema,
    status: 200,
    response: pushResponseSchema,
    errors: ["APP_UPGRADE_REQUIRED"],
});
/**
 * `POST /v1/pos/sync/recover` (B-52): the unsent commands of an earlier pairing at the same branch, found in an
 * archived database on this computer, sent by the new device with a manager's approval. They keep their own
 * `mutationId`/`deviceSeq` and are processed under the source device's seq state; each new one also gets `RECOVERED`.
 */
export const recoverRequestSchema = z
    .object({
    sourceDeviceId: z.uuid(),
    /** The manager who approved the recovery on the device (PIN checked there; `pos.approve` checked here). */
    approvedByUserId: z.uuid(),
    sentAt: z.iso.datetime(),
    commands: z.array(commandEnvelopeSchema).min(1).max(PUSH_BATCH_MAX),
    appVersion: appVersionSchema.optional(),
})
    .strict();
export const recoverCommands = defineEndpoint({
    tag: "pos-sync",
    name: "recoverCommands",
    method: "POST",
    path: "/v1/pos/sync/recover",
    access: { device: true },
    summary: "Unsent commands of an earlier device at this branch, from its old database; processed as that device, each reviewed",
    body: recoverRequestSchema,
    status: 200,
    response: pushResponseSchema,
    errors: ["BUSINESS_RULE_VIOLATION", "APP_UPGRADE_REQUIRED"],
});
//# sourceMappingURL=commands.js.map